import React from 'react';
import {
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Switch,
  Select,
  FormHelperText,
  InputGroup,
  InputRightElement,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';

interface EmailChannelConfigProps {
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
}

export const EmailChannelConfig: React.FC<EmailChannelConfigProps> = ({
  config,
  onChange,
}) => {
  const { isOpen: showPassword, onToggle: togglePassword } = useDisclosure();

  const updateConfig = (key: string, value: any) => {
    onChange({
      ...config,
      [key]: value,
    });
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* SMTP Server Settings */}
      <HStack spacing={4}>
        <FormControl isRequired>
          <FormLabel>SMTP Host</FormLabel>
          <Input
            value={config.host || ''}
            onChange={(e) => updateConfig('host', e.target.value)}
            placeholder="smtp.gmail.com"
          />
        </FormControl>

        <FormControl isRequired width="150px">
          <FormLabel>Port</FormLabel>
          <NumberInput
            value={config.port || 587}
            onChange={(_, value) => updateConfig('port', value || 587)}
            min={1}
            max={65535}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </HStack>

      {/* Authentication */}
      <FormControl>
        <FormLabel>Username</FormLabel>
        <Input
          value={config.username || ''}
          onChange={(e) => updateConfig('username', e.target.value)}
          placeholder="your-email@example.com"
        />
        <FormHelperText>
          Leave empty if authentication is not required
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel>Password</FormLabel>
        <InputGroup>
          <Input
            type={showPassword ? 'text' : 'password'}
            value={config.password || ''}
            onChange={(e) => updateConfig('password', e.target.value)}
            placeholder="Your SMTP password or app password"
          />
          <InputRightElement>
            <IconButton
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
              variant="ghost"
              size="sm"
              onClick={togglePassword}
            />
          </InputRightElement>
        </InputGroup>
        <FormHelperText>
          Use app-specific passwords for Gmail, Yahoo, etc.
        </FormHelperText>
      </FormControl>

      {/* Security Settings */}
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Encryption</FormLabel>
          <Select
            value={config.encryption || 'tls'}
            onChange={(e) => updateConfig('encryption', e.target.value)}
          >
            <option value="none">None</option>
            <option value="tls">TLS/STARTTLS</option>
            <option value="ssl">SSL</option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Verify SSL</FormLabel>
          <Switch
            isChecked={config.verifySsl !== false}
            onChange={(e) => updateConfig('verifySsl', e.target.checked)}
            colorScheme="primary"
          />
        </FormControl>
      </HStack>

      {/* Sender Information */}
      <FormControl isRequired>
        <FormLabel>From Email</FormLabel>
        <Input
          value={config.fromEmail || ''}
          onChange={(e) => updateConfig('fromEmail', e.target.value)}
          placeholder="noreply@yourcompany.com"
        />
        <FormHelperText>
          The email address that will appear as the sender
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel>From Name</FormLabel>
        <Input
          value={config.fromName || ''}
          onChange={(e) => updateConfig('fromName', e.target.value)}
          placeholder="Your Company Name"
        />
        <FormHelperText>
          The name that will appear as the sender
        </FormHelperText>
      </FormControl>

      {/* Connection Settings */}
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Max Connections</FormLabel>
          <NumberInput
            value={config.maxConnections || 5}
            onChange={(_, value) => updateConfig('maxConnections', value || 5)}
            min={1}
            max={20}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>

        <FormControl>
          <FormLabel>Timeout (seconds)</FormLabel>
          <NumberInput
            value={config.timeout || 30}
            onChange={(_, value) => updateConfig('timeout', value || 30)}
            min={5}
            max={300}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </HStack>

      {/* Rate Limiting */}
      <HStack spacing={4}>
        <FormControl>
          <FormLabel>Rate Limit (emails/minute)</FormLabel>
          <NumberInput
            value={config.rateLimit || 60}
            onChange={(_, value) => updateConfig('rateLimit', value || 60)}
            min={1}
            max={1000}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>

        <FormControl>
          <FormLabel>Max Retries</FormLabel>
          <NumberInput
            value={config.maxRetries || 3}
            onChange={(_, value) => updateConfig('maxRetries', value || 3)}
            min={0}
            max={10}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
      </HStack>
    </VStack>
  );
};